import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Volume2, Star, HelpCircle, RotateCcw } from 'lucide-react';
import { speak as _speakTTS, stopSpeaking as _stopTTS } from '@/utils/speak';

const MultipleChoiceQuiz = ({ questions, onComplete, childId }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState([]);

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex) / questions.length) * 100;

  const speakText = (text) => {
    _speakTTS(text, 0.85);
  };

  const handleAnswer = (answer) => {
    if (showFeedback) return;
    
    setSelectedAnswer(answer);
    setShowFeedback(true);
    
    const isCorrect = answer === currentQuestion.answer;
    setAnswers([...answers, { question: currentQuestion.question, answer, isCorrect }]);
    
    if (isCorrect) {
      setScore(s => s + 1);
      toast.success('Correct! Great job!', { duration: 1500 });
    } else {
      toast.error(`Not quite! The answer is ${currentQuestion.answer}`, { duration: 2000 });
    }

    setTimeout(() => {
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(i => i + 1);
        setSelectedAnswer(null);
        setShowFeedback(false);
        setShowHint(false);
      } else {
        setCompleted(true);
        const finalScore = Math.round(((score + (isCorrect ? 1 : 0)) / questions.length) * 100);
        if (onComplete) {
          onComplete(finalScore, answers.length + 1);
        }
      }
    }, 1500);
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setShowHint(false);
    setCompleted(false);
    setAnswers([]);
  };

  const getOptionColor = (option) => {
    if (!showFeedback) return 'bg-white hover:bg-sky-50 border-slate-200';
    if (option === currentQuestion.answer) return 'bg-green-100 border-green-500 text-green-800';
    if (option === selectedAnswer && option !== currentQuestion.answer) return 'bg-red-100 border-red-500 text-red-800';
    return 'bg-white border-slate-200 opacity-50';
  };

  if (completed) {
    const percentage = Math.round((score / questions.length) * 100);
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center">
          <Star className="w-12 h-12 text-white" />
        </div>
        <h2 className="font-fredoka text-3xl font-bold text-purple-800 mb-2">Quiz Complete!</h2>
        <p className="text-xl text-purple-600 mb-4">You scored {score} out of {questions.length}</p>
        <div className="w-48 mx-auto mb-6">
          <Progress value={percentage} className="h-4" />
          <p className="text-sm text-slate-500 mt-1">{percentage}%</p>
        </div>
        <div className="flex justify-center gap-4">
          <Button onClick={handleReset} variant="outline" className="gap-2">
            <RotateCcw className="w-4 h-4" /> Try Again
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6" data-testid="multiple-choice-quiz">
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-slate-500">
          <span>Question {currentIndex + 1} of {questions.length}</span>
          <span>{score} correct</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Question Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
        >
          <Card className="bg-gradient-to-br from-sky-50 to-blue-50 border-sky-200">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-fredoka text-xl font-bold text-sky-800 flex-1">
                  {currentQuestion.question}
                </h3>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => speakText(currentQuestion.question)}
                    className="text-sky-600"
                  >
                    <Volume2 className="w-5 h-5" />
                  </Button>
                  {currentQuestion.hint && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowHint(!showHint)}
                      className="text-amber-600"
                    >
                      <HelpCircle className="w-5 h-5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Hint */}
              {showHint && currentQuestion.hint && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4"
                >
                  <p className="text-amber-800 text-sm">💡 Hint: {currentQuestion.hint}</p>
                </motion.div>
              )}

              {/* Image if present */}
              {currentQuestion.image && (
                <div className="mb-4 flex justify-center">
                  <img 
                    src={currentQuestion.image} 
                    alt="Question" 
                    className="max-h-48 rounded-lg shadow-md"
                  />
                </div>
              )}

              {/* Options */}
              <div className="grid grid-cols-2 gap-3">
                {currentQuestion.options.map((option, i) => (
                  <motion.button
                    key={option}
                    whileHover={{ scale: showFeedback ? 1 : 1.02 }}
                    whileTap={{ scale: showFeedback ? 1 : 0.98 }}
                    onClick={() => handleAnswer(option)}
                    disabled={showFeedback}
                    className={`p-4 rounded-xl border-2 font-bold text-lg transition-all ${getOptionColor(option)}`}
                    data-testid={`option-${i}`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {showFeedback && option === currentQuestion.answer && (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                      {showFeedback && option === selectedAnswer && option !== currentQuestion.answer && (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                      {option}
                    </div>
                  </motion.button>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default MultipleChoiceQuiz;
